import * as React from 'react';
import hookdWhiteLogo from '../static/hookdwhitelogo.png';
import youTubeImage from '../static/youtube.png';
import abbeyRoadImage from '../static/abbey_road.png';
import midemImage from '../static/midemlab-finalist.png';
import { PlayerBar } from '../components/player/player-bar';
import { Header } from '../components/header/header';
import { Component, ComponentProps, deps, inject, observer } from '../lib/component';
import { FlashContainer } from '../components/flash/container';
import { Head } from './head';
import { JustAdded } from './just-added';
import { Modals } from './modals';
import { FollowCompat } from '../components/compat/follow-compat';
import { AppShuntCTA } from '../components/app-shunt-cta';
import { AppFooter } from '../components/app-footer';
import { AppCookieNotification } from '../components/app-cookie-notification';
import { constantHeaderHeight, constantDrawerWidth, constantExpandedHeaderHeight } from '../theme/constant';
import { classes, media, style } from 'typestyle';
import { mediaDesktop, mediaMobileOnly, mediaUpToTablet } from '../theme/media';
import { important, percent, px, viewHeight } from 'csx';
import AppMenuDrawer from '../components/app-menu-drawer';
import { colorBlack, colorWhite } from '../theme/color';
import { zAppMenuDrawer } from '../theme/z';
import { betweenJustified, vertical } from 'csstips';
import { BasketRestrictedModal } from '../components/basket-restricted-modal';
import FeatureFlagWidget from "../components/devtools/feature-flag-widget";

@inject(deps)
@observer
export class MasterContainer extends Component<ComponentProps> {
  render() {
    const {
      showPlayer,
      showChrome,
      firstPaint,
      cookieNotification,
      showMenuDrawer,
      showBreadcrumbs,
    } = this.props.model.ui;
    const content = this.props.model.content;

    if (!showChrome)
      return (
        <span>
          <Head />
          {React.Children.only(this.props.children)}
          <Modals />
        </span>
      );

    const { exampleLicenseUri } = this.props.model.env;

    const { signUpCTAShow } = this.props.model.onboarding;
    const { openSignUpModal, dismissSignUpCTA } = this.props.controller.onboarding;

    const mainClasses = classes(
      MasterContainer.styles.main,
      showMenuDrawer && MasterContainer.styles.mainShunted,
      showBreadcrumbs && MasterContainer.styles.mainHeaderExpanded,
    );

    return (
      <div className={MasterContainer.styles.master}>
        <div
          className={classes(MasterContainer.styles.blanket, showMenuDrawer && MasterContainer.styles.blanketVisible)}
          onClick={this.hideDrawer}
        />
        <Head />
        {!firstPaint && content.fetched && (
          <AppShuntCTA
            onClickSignUp={() => openSignUpModal()}
            onClickClose={() => dismissSignUpCTA()}
            show={signUpCTAShow}
            content={content.shuntCTA}
          />
        )}
        <Header />
        <AppMenuDrawer />
        <main className={mainClasses}>
          {this.props.children}
          <JustAdded />
          <AppFooter
            apiContent={content.apiContent}
            expanded={showPlayer}
            abbeyRoadImage={abbeyRoadImage}
            youTubeImage={youTubeImage}
            logoImage={hookdWhiteLogo}
            midemImage={midemImage}
            onClickLink={this.handleClickLink}
            Follow={FollowCompat}
            exampleLicense={exampleLicenseUri}
          />
          <FlashContainer />
        </main>
        <PlayerBar visible={showPlayer} />
        <Modals />
        {!firstPaint && (
          <AppCookieNotification
            model={cookieNotification}
            onClose={this.props.controller.ui.dismissCookieNotification}
            onClickLink={this.handleClickLink}
          />
        )}
        <FeatureFlagWidget />
      </div>
    );
  }

  componentDidMount() {
    // Used to ensure client-side-only components can render after first paint
    this.props.model.ui.firstPaint = false;
    if (!this.props.model.env.isServer) {
      window.addEventListener('hashchange', this.offsetAnchor);
      setTimeout(this.offsetAnchor, 1);
    }
    this.props.model.basket.restrictedTracks.length > 0 && this.props.controller.modal.showBasketRestrictedModal();
  }

  componentWillUnmount() {
    if (!this.props.model.env.isServer) {
      window.removeEventListener('hashchange', this.offsetAnchor);
    }
  }

  handleClickLink = (event: React.MouseEvent<any>) => {
    const el = event.currentTarget as HTMLAnchorElement;
    const local = el.host === document.location.host;

    switch (true) {
      case el.pathname.includes('/blog') === true:
        return;

      case el.pathname === '#':
        return event.preventDefault();

      case local:
        event.preventDefault();
        event.stopPropagation();
        return this.props.model.router.push(el.pathname);
    }
  };

  offsetAnchor = () => {
    const { hash } = this.props.model.router.location;
    if (hash.length > 0) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        const offset = parseInt(constantHeaderHeight.toString());
        const { top } = this.getCoords(el);
        window.scrollTo(window.scrollX, Math.max(0, top - offset));
      }
    }
  };

  getCoords = (elem: Element) => {
    const box = elem.getBoundingClientRect();

    const body = document.body;
    const docEl = document.documentElement;

    const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

    const clientTop = docEl.clientTop || body.clientTop || 0;
    const clientLeft = docEl.clientLeft || body.clientLeft || 0;

    const top = box.top + scrollTop - clientTop;
    const left = box.left + scrollLeft - clientLeft;

    return { top: Math.round(top), left: Math.round(left) };
  };

  hideDrawer = () => {
    this.props.controller.ui.setMenuDrawer(false);
  };

  static styles = {
    master: style({
      minHeight: viewHeight(100),
      overflowX: 'hidden',
      $nest: {
        '@media all and (-ms-high-contrast:none)': {
          height: '100vh !important',
        },
      },
    }),
    main: style({
      ...vertical,
      ...betweenJustified,
      marginTop: constantHeaderHeight,
      width: percent(100),
      background: colorWhite.toString(),
      position: 'relative',
      overflowX: 'hidden',
      minHeight: '100vh',
      padding: 0,
      $nest: {
        '@media all and (-ms-high-contrast:none)': {
          height: '100vh !important',
          flexShrink: important(0) as any,
        },
      },
    }),
    mainHeaderExpanded: style(
      mediaMobileOnly({
        marginTop: constantExpandedHeaderHeight,
      }),
    ),
    mainShunted: style(
      mediaDesktop({
        width: important(`calc(100% - ${constantDrawerWidth})`),
        marginRight: constantDrawerWidth,
      }),
    ),
    blanket: style(
      mediaUpToTablet({
        position: 'fixed',
        display: 'none',
        top: px(-100),
        bottom: px(-100),
        left: px(-100),
        right: px(-100),
        background: colorBlack.fade(0.8).toString(),
        zIndex: zAppMenuDrawer - 1,
      }),
      mediaDesktop({
        display: 'none',
      }),
    ),
    blanketVisible: style(
      mediaUpToTablet({
        display: 'block',
      }),
    ),
  };
}
